import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import { FaPhoneAlt } from "react-icons/fa";
import { MdEmail } from "react-icons/md";
import { IoMdArrowDropdown } from "react-icons/io";
import { IoMdArrowDropup } from "react-icons/io";

function LandingPage() {
    const [cardsData, setCardsData] = useState([]);
    const [allProfessions, setAllProfessions] = useState({});
    const [selectedProfession, setSelectedProfession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const { contactId } = useParams();
    console.log("contactId--", contactId);
    

    const [userId, setUserId] = useState("");
    const [memberPhoneNumber, setMemberPhoneNumber] = useState("");
    const [memberEmail, setMemberEmail] = useState("");
    const [expanded, setExpanded] = useState(false);

    const formatPhoneNumber = (phone) => {
        if (!phone) return '';
        const digits = phone.replace(/\D/g, '');
        if (digits.length !== 10) return phone;
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    };

    const fetchProfessions = async () => {
        try {
            const res = await fetch('https://api.proinsight.com/public_professiondetails', {
                method: 'POST',
                headers: { Accept: 'application/json' },
            });
            if (!res.ok) throw new Error('Failed to load professions');
            const data = await res.json();
            setAllProfessions(data || {});
        } catch (err) {
            console.error("fetchProfessions error:", err);
            setError(prev => prev || 'Unable to load profession categories.');
        }
    };

    const fetchMemberDetails = async () => {
        if (!contactId) {
            setError('No contact ID provided in the URL');
            setLoading(false);
            return;
        }

        let formattedContactId = contactId.replace("&", "-")

        try {
            const res = await fetch(`https://api.proinsight.com/get_user_by_name?name=${formattedContactId}`);
            if (!res.ok) {
                setLoading(false);
                throw new Error(`HTTP ${res.status}: Failed to load member details`);
            }
            const data = await res.json();

            if (!data?.user_id) {
                throw new Error('User ID not found in response');
            }

            setUserId(data.user_id);
            setMemberEmail(data.email || '');
            setMemberPhoneNumber(data.phone || '');
        } catch (err) {
            console.error("fetchMemberDetails error:", err);
            setLoading(false);
            setError('Could not load professionals data.');
        }
    };

    const fetchCards = async (uid) => {
        if (!uid) return;

        try {
            const res = await fetch('https://api.proinsight.com/get_card_details', {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ user_id: uid }),
            });

            if (!res.ok) {
                throw new Error(`HTTP ${res.status}: Failed to load professionals`);
            }

            const data = await res.json();
            setCardsData(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("fetchCards error:", err);
            setError('No professionals found or API error.');
        }
    };

    // Phase 1: Load member details + professions (independent)
    useEffect(() => {
        const init = async () => {
            setLoading(true);
            setError(null);

            await Promise.all([
                fetchMemberDetails(),
                fetchProfessions(),
            ]);
            // Note: we do NOT set loading false here — we wait for cards too
        };

        init();
    }, [contactId]);

    // Phase 2: Load cards only after we have a valid userId
    useEffect(() => {
        if (!userId) return;

        const loadCards = async () => {
            await fetchCards(userId);
            setLoading(false); // only finish loading after cards are fetched
        };

        loadCards();
    }, [userId]);

    // ── Computed values ─────────────────────────────────────────────

    const availableProfessions = useMemo(() => {
        if (!cardsData.length) return {};

        const professionSet = new Set(cardsData.map(card => card.profession));

        const filtered = {};
        Object.entries(allProfessions).forEach(([groupName, profList]) => {
            const matching = profList.filter(prof => professionSet.has(prof));
            if (matching.length > 0) {
                filtered[groupName] = matching;
            }
        });

        return filtered;
    }, [cardsData, allProfessions]);

    const selectOptions = useMemo(() => {
        const opts = [{ value: 'all', label: 'All Professionals' }];

        Object.entries(availableProfessions).forEach(([groupName, profList]) => {
            opts.push({
                label: groupName,
                options: profList.map(prof => ({
                    value: prof,
                    label: prof,
                })),
            });
        });

        return opts;
    }, [availableProfessions]);

    const currentSelection =
        selectedProfession?.value === 'all' || !selectedProfession
            ? 'all'
            : selectedProfession.value;

    const filteredCards =
        currentSelection === 'all'
            ? cardsData
            : cardsData.filter(card => card.profession === currentSelection);

    // ── JSX ─────────────────────────────────────────────────────────

    return (
        <div className="landingPageContainer">
            <div className="heroContainer" style={{ zIndex: 2 }}>
                <div className="headerSection">
                    <img
                        style={{ width: '200px', height: '70px', margin: ".5rem 2rem" }}
                        className="logo"
                        src="https://images.leadconnectorhq.com/image/f_webp/q_80/r_1200/u_https://assets.cdn.filesafe.space/sIF5ZS1VxhUZsYbvq7gL/media/68e000abea73aa71abf05459.png"
                        alt="Logo"
                    />
                    <div className="phoneEmailSection">
                        <div style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
                            <FaPhoneAlt style={{ color: "#E4BC14", fontSize: "20px" }} />
                            <a

                                href={`tel:${memberPhoneNumber}`}
                                style={{ fontWeight: "500", color: "#C1C2C3", fontSize: "18px" }}
                            >
                                {formatPhoneNumber(memberPhoneNumber) || ((userId && !memberPhoneNumber) ? "N/A" : <div className="skeleton skel-line" style={{ width: '100px', background: "#A5B8C3", height: "17px", paddingTop: "3px" }}></div>)}
                            </a>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: ".5rem", marginTop: "5px" }}>
                            <MdEmail style={{ color: "#E4BC14", fontSize: "20px" }} />
                            <span style={{ fontWeight: "500", color: "#C1C2C3", fontSize: "18px" }}>
                                {memberEmail || ((userId && !memberEmail) ? "N/A" : <div className="skeleton skel-line" style={{ width: '180px', background: "#A5B8C3", height: "17px", paddingTop: "3px" }}></div>)}
                            </span>
                        </div>
                    </div>
                </div>
                <div className="heroTitle">Homeowner Concierge™</div>
                <div className="backgroundImgDiv" style={{ zIndex: -1 }}></div>
            </div>

            <div className="middleSection">
                <div style={{ display: "flex", width: "75%", justifyContent: "center" }} className='downloadAppSection' >
                    <div>
                        <img style={{ width: "400px" }} className='downloadAppSectionImg' src='https://assets.cdn.filesafe.space/sIF5ZS1VxhUZsYbvq7gL/media/69b066f5d9becc1706540257.png' />
                    </div>
                    <div >
                        <div style={{ color: 'black', fontWeight: '500', fontSize: '22px', textAlign: "center" }} className='downloadAppText' >
                            Skip the searching. Get priority access to trusted, accountable professionals in one simple app.
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                            <div
                                onClick={() => window.open(`https://homeowner.proinsight.com/${contactId}?userId=${userId}`, '_blank')}

                                style={{
                                    padding: '1rem',
                                    background: '#E93D3D',
                                    width: '200px',
                                    borderRadius: '5px',
                                    marginTop: '2rem',
                                    textAlign: 'center',
                                    cursor: "pointer",
                                    pointerEvents: !userId && "none",
                                    opacity: !userId && "0.6"
                                }}
                            >
                                <div style={{ color: 'white', fontSize: '18px', fontFamily: "Roboto" }}>
                                    Setup Your App
                                </div>
                                <div style={{ color: 'white', fontSize: '16px', fontWeight: '300', fontFamily: "Roboto" }}>
                                    Takes One Minute
                                </div>
                            </div>
                        </div>
                    </div>
                </div>


                {/* Cards Section */}
                <div className="vendorSectionContainer" style={{ margin: '2rem auto', padding: '0 1rem' }}>
                    <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'center' }}>
                        <div style={{ textAlign: "start" }}>
                            <Select
                                options={selectOptions}
                                value={selectedProfession || { value: 'all', label: 'Search for Professionals' }}
                                onChange={setSelectedProfession}
                                isSearchable
                                className='professionsDropdown'
                                isDisabled={loading || Object.keys(availableProfessions).length === 0}
                                placeholder="Search or select profession..."
                                noOptionsMessage={() => 'No professions available'}
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        height: '57px',
                                        minHeight: '57px',
                                        borderRadius: '6px',
                                        borderColor: '#ccc',
                                        fontSize: '16px',
                                        minWidth: "300px",
                                        maxWidth: "380px",
                                        textAlign: "start",
                                    }),
                                    option: (base) => ({
                                        ...base,
                                        fontSize: '16px',
                                    }),
                                    groupHeading: (base) => ({
                                        ...base,
                                        fontWeight: 700,
                                        fontSize: '17px',
                                        color: '#333',
                                    }),
                                }}
                            />
                        </div>
                    </div>

                    <div style={{ margin: '1rem 0rem', display: 'flex', justifyContent: 'center', width: "100%" }}>
                    <div
                        className='footerDisclaimer'
                        style={{ width: '92%', padding: '0 1rem', fontSize: '15px', lineHeight: 1.6 }}
                    >
                        <div style={{ color: '#333333', fontWeight: '600', fontSize: "16px", lineHeight: 1.5, textAlign: "center" }}>
                            {!expanded && (
                                <>
                                    {" "}
                                    <span
                                        onClick={() => setExpanded(true)}
                                        style={{
                                            color: '#1e40af',
                                            cursor: 'pointer',
                                            fontWeight: '500',
                                            textDecoration: 'underline',
                                        }}
                                    >
                                       Read full disclaimer ▼
                                    </span>
                                </>
                            )}
                        </div>

                        {expanded && (
                            <>
                                <div style={{ color: '#333333', fontWeight: '600', fontSize: "16px", lineHeight: 1.5, marginTop: "1rem" }}>
                                    Disclaimer
                                </div>
                                <div style={{ color: '#333333', fontWeight: '600', fontSize: "16px", lineHeight: 1.5, marginTop: "1rem" }}>
                                    ProInsight™ and its affiliates make no representations or warranties regarding the accuracy, completeness, suitability, or content of any advertisements, profiles, recommendations, or other information presented on this page.
                                </div>
                                <div style={{ color: '#333333', fontWeight: '600', fontSize: "16px", lineHeight: 1.5, marginTop: "1rem" }}>
                                    ProInsight™ and its affiliates assume no responsibility or liability for the conduct of any service provider, the quality of any products or services offered, or any statements, promises, or representations made by such service providers.
                                </div>

                                <div style={{ color: '#333333', fontWeight: '600', fontSize: "16px", lineHeight: 1.5, marginTop: "1rem"  }}>
                                    ProInsight™ and its affiliates do not guarantee that any service provider is licensed, bonded, insured, qualified, or in good standing. Clients are solely responsible for conducting their own due diligence, including independently verifying a provider’s licensing, insurance, and bonding status before engaging their services.
                                </div>

                                <div style={{ color: '#333333', fontWeight: '600', fontSize: "16px", lineHeight: 1.5, marginTop: "1rem"  }}>
                                    Use of any service provider is entirely voluntary. ProInsight™ and its affiliates do not require, mandate, or guarantee the use of any particular provider. The selection and engagement of any service provider is solely at the client’s discretion and risk.
                                </div>

                                <span
                                    onClick={() => setExpanded(false)}
                                    style={{
                                        color: '#1e40af',
                                        cursor: 'pointer',
                                        fontWeight: '500',
                                        textDecoration: 'underline',
                                        display: 'inline-block',
                                        marginTop: '0.5rem',
                                        marginBottom : "2rem"
                                    }}
                                >
                                   Show less ▲
                                </span>
                            </>
                        )}
                    </div>
                </div>

                    {loading ? (
                        <div className="grid">
                            {Array.from({ length: 6 }).map((_, i) => (
                                <div key={i} className="skeleton-card" style={{ width: "350px" }} >
                                    <div className="skeleton skel-logo"></div>
                                    <div className="skeleton skel-line"></div>
                                    <div className="skeleton skel-line" style={{ width: '40%' }}></div>
                                    <div className="skeleton skel-line" style={{ width: '50%' }}></div>
                                </div>
                            ))}
                        </div>
                    ) : error ? (
                        <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#555' }}>
                            <h3 style={{ color: '#000', marginBottom: '1rem', fontSize: "18px" }}>
                                Error
                            </h3>
                            <p style={{ fontSize: "16px" }}>{error}</p>
                        </div>
                    ) : filteredCards.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#555' }}>
                            <h3 style={{ color: '#000', marginBottom: '1rem', fontSize: "18px" }}>
                                No Results
                            </h3>
                            <p style={{ fontSize: "16px" }}>
                                {currentSelection === 'all'
                                    ? 'No professionals found yet.'
                                    : `No professionals found in "${currentSelection}".`}
                            </p>
                        </div>
                    ) : (
                        <div className="grid" id="card-container">
                            {filteredCards.map((card, index) => {
                                const websiteUrl = card.website_url || '';
                                const firstWebsite = websiteUrl.split(',')[0]?.trim() || '';

                                return (
                                    <div className="card" key={index}>
                                        {card.logo && !card.seeduser && (
                                            <div style={{ marginBottom: '1rem' }}>
                                                <a
                                                    href={firstWebsite}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                        display: 'flex',
                                                        pointerEvents: firstWebsite ? 'auto' : 'none',
                                                        justifyContent: "center"
                                                    }}
                                                >
                                                    <img
                                                        src={card.logo}
                                                        alt="Company Logo"
                                                        style={{ maxHeight: '70px',  objectFit: 'contain', background: "white" }}
                                                    />
                                                </a>
                                            </div>
                                        )}

                                        {card.seeduser && (
                                            <div style={{ marginBottom: '1rem' }}>
                                                <a
                                                    href={firstWebsite}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                        display: 'flex', 
                                                        pointerEvents: firstWebsite ? 'auto' : 'none',
                                                        justifyContent: "center"
                                                    }}
                                                >
                                                    <img
                                                        src={"https://proinsightblobwestus.blob.core.windows.net/avatars/57a6e188-f359-4cdb-8e69-733bbe4fa898&t=2026-03-11T18_09_49"}
                                                        alt="Company Logo"
                                                        style={{ maxHeight: '70px', objectFit: 'contain', background: "white" }}
                                                    />
                                                </a>
                                            </div>
                                        )}

                                        <div style={{padding: "1.5rem"}} >
                                            <h3>
                                                {card.business || 'N/A'} — {card.fullname || 'N/A'}
                                            </h3>

                                            <p style={{ color: "#535151" }} >{card?.profession || ""}</p>
                                            <hr style={{ margin: '0.8rem 0', borderColor: '#ddd', marginLeft: "70%" }} />


                                            <p style={{ margin: '0.6rem 0' }}>
                                                <a
                                                    href={`tel:${card.phone}`}
                                                    style={{ textDecoration: 'none', color: '#000', fontWeight: 500 }}
                                                >
                                                    {formatPhoneNumber(card.phone) || '—'}
                                                </a>
                                            </p>

                                            <p style={{ margin: '0.4rem 0', color: '#444' }}>
                                                {card.email || '—'}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>


            </div>

            <div className="landingPageFooter">
                <div style={{ color: 'white', fontSize: "16px", marginLeft: "10px" }}>
                    Powered by:
                </div>
                <img
                    style={{ width: '250px' }}
                    src="https://images.leadconnectorhq.com/image/f_webp/q_80/r_1200/u_https://assets.cdn.filesafe.space/sIF5ZS1VxhUZsYbvq7gL/media/69322a9c1d466ea1bdb5dea8.png"
                    alt="Powered by logo"
                />
            </div>
        </div>
    );
}

export default LandingPage;